import { useState, useMemo } from "react";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Globe,
  User,
  ChevronDown,
  ChevronRight,
  Copy,
  Settings2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageLayout } from "@/components/templates/page-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TiptapEditor, type VariableChip } from "@/components/organisms/tiptap-editor";
import { useLibrary, type LibrarySection } from "@/core/hooks/useLibrary";
import type { JSONContent } from "@tiptap/react";

// Variables dynamiques disponibles dans les blocs librairie. Toutes proposées
// car un bloc librairie peut être inséré n'importe où dans le rapport.
// Doit rester en miroir des chips de rapport-edit.tsx (CHIPS_SITE + CHIPS_STATS).
const LIBRARY_CHIPS: VariableChip[] = [
  { key: "site", label: "Nom du site" },
  { key: "adresse", label: "Adresse" },
  { key: "date", label: "Date campagne" },
  { key: "nb_mesures", label: "Nb mesures" },
  // Variable redactionnelle : une phrase par couple reseau x materiau, valeurs
  // min/max/moyenne colorees selon le code couleur (cf. tiptap-to-pdfmake.ts).
  // Version "par typologie" qui remplace les ex-stats GLOBALES retirees le
  // 2026-06-03 (on ne melange jamais acier et cuivre).
  { key: "mesures_min_max_moy", label: "Mesures rédigées par réseau" },
];

// ─── Catégories suggérées (Renaud peut en taper des nouvelles) ───

const DEFAULT_CATEGORIES = [
  "mission",
  "rappel-normes",
  "conclusions",
  "analyses-eau",
  "principe-mesure",
  "custom",
];

const CATEGORY_LABELS: Record<string, string> = {
  mission: "Mission",
  "rappel-normes": "Rappel des normes",
  conclusions: "Conclusions",
  "analyses-eau": "Analyses eau",
  "principe-mesure": "Principe de la mesure",
  custom: "Mes blocs",
};

function labelFor(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

// ─── Preview texte ────────────────────────────────────────────

function extractPreview(content: unknown): string {
  try {
    const doc = content as { type: string; content?: unknown[] };
    for (const node of doc.content ?? []) {
      const n = node as {
        type: string;
        content?: Array<{ type: string; text?: string }>;
      };
      if (n.type === "paragraph" || n.type === "heading") {
        const text = (n.content ?? [])
          .filter((c) => c.type === "text" && c.text)
          .map((c) => c.text)
          .join("");
        if (text.trim())
          return text.trim().slice(0, 120) + (text.length > 120 ? "…" : "");
      }
    }
  } catch {
    // fallback
  }
  return "";
}

// ─── Formulaire create / edit ─────────────────────────────────

interface SectionFormProps {
  initial?: Pick<LibrarySection, "title" | "content" | "category">;
  categorySuggestions: string[];
  onSave: (data: {
    title: string;
    content: JSONContent;
    category: string;
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

function SectionForm({
  initial,
  categorySuggestions,
  onSave,
  onCancel,
  loading,
}: SectionFormProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  // Pas de valeur par defaut "custom" : un input pre-rempli avec une valeur
  // qui matche exactement une option du datalist ferme la dropdown au focus
  // (comportement HTML5 documente, plus instable encore sur Safari). En mode
  // creation, on laisse vide pour que toutes les categories soient visibles
  // au clic ; en mode edition, on garde la categorie actuelle.
  const [category, setCategory] = useState(initial?.category ?? "");
  const [content, setContent] = useState<JSONContent | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    if (!category.trim()) {
      setError("La catégorie est requise.");
      return;
    }
    const finalContent =
      content ??
      (initial?.content as JSONContent) ?? { type: "doc", content: [] };
    setError(null);
    await onSave({
      title: title.trim(),
      content: finalContent,
      category: category.trim(),
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <Label htmlFor="lib-title">Titre</Label>
        <Input
          id="lib-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Ex : Conclusion fiabilité réseau…"
          required
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="lib-category">Catégorie</Label>
        <Input
          id="lib-category"
          list="lib-category-suggestions"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          placeholder="Choisir ou créer une catégorie"
          required
        />
        <datalist id="lib-category-suggestions">
          {categorySuggestions.map((c) => (
            <option key={c} value={c} />
          ))}
        </datalist>
        <p className="mt-1 text-xs text-muted-foreground">
          Tape une catégorie existante ou un nouveau nom (libre).
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <Label>Contenu</Label>
        <TiptapEditor
          initialContent={initial?.content as JSONContent | undefined}
          onChange={(c) => setContent(c)}
          minHeight="150px"
          variableChips={LIBRARY_CHIPS}
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex gap-2">
        <Button type="submit" size="sm" loading={loading}>
          <Check className="size-4" />
          Enregistrer
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          <X className="size-4" />
          Annuler
        </Button>
      </div>
    </form>
  );
}

// ─── Carte d'un bloc ──────────────────────────────────────────

interface SectionCardProps {
  section: LibrarySection;
  editMode: boolean;
  onEdit: (s: LibrarySection) => void;
  onDuplicate: (s: LibrarySection) => void;
  onDelete: (id: string) => void;
}

function SectionCard({
  section,
  editMode,
  onEdit,
  onDuplicate,
  onDelete,
}: SectionCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const preview = extractPreview(section.content);

  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="mb-1 flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-foreground leading-tight">
          {section.title}
        </p>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
            section.isGlobal
              ? "bg-muted text-muted-foreground"
              : "bg-primary/10 text-primary"
          }`}
        >
          {section.isGlobal ? (
            <>
              <Globe className="size-3" /> Standard
            </>
          ) : (
            <>
              <User className="size-3" /> Perso
            </>
          )}
        </span>
      </div>

      {preview && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {preview}
        </p>
      )}

      {editMode && (
        <div className="flex flex-wrap gap-2 mt-2">
          {confirmDelete ? (
            <>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(section.id)}
              >
                Confirmer
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(false)}
              >
                Annuler
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => onEdit(section)}>
                <Pencil className="size-3" />
                Modifier
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDuplicate(section)}
              >
                <Copy className="size-3" />
                Dupliquer
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="size-3" />
                Supprimer
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────

export default function LibraryPage() {
  const navigate = useNavigate();
  const {
    sections,
    loading,
    error,
    createSection,
    updateSection,
    deleteSection,
  } = useLibrary();

  const [editMode, setEditMode] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSection, setEditingSection] = useState<LibrarySection | null>(
    null
  );
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Groupes par catégorie
  const byCategory = useMemo(() => {
    return sections.reduce<Record<string, LibrarySection[]>>((acc, s) => {
      const cat = s.category ?? "custom";
      (acc[cat] ??= []).push(s);
      return acc;
    }, {});
  }, [sections]);

  // Ordre des catégories : prédéfinies d'abord, puis nouvelles par ordre alpha
  const orderedCategories = useMemo(() => {
    const present = Object.keys(byCategory);
    const known = DEFAULT_CATEGORIES.filter((c) => present.includes(c));
    const extras = present.filter((c) => !DEFAULT_CATEGORIES.includes(c)).sort();
    return [...known, ...extras];
  }, [byCategory]);

  // Suggestions catégories pour le formulaire
  const categorySuggestions = useMemo(() => {
    const set = new Set<string>([...DEFAULT_CATEGORIES, ...Object.keys(byCategory)]);
    return Array.from(set);
  }, [byCategory]);

  function toggleCategory(cat: string) {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  async function handleCreate(data: {
    title: string;
    content: JSONContent;
    category: string;
  }) {
    setSaving(true);
    setActionError(null);
    try {
      await createSection(data);
      setShowCreateForm(false);
      // Ouvrir la catégorie où le bloc vient d'être créé
      setOpenCategories((prev) => new Set(prev).add(data.category));
    } catch {
      setActionError("Impossible de créer le bloc. Vérifiez votre connexion.");
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(data: {
    title: string;
    content: JSONContent;
    category: string;
  }) {
    if (!editingSection) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateSection(editingSection.id, data);
      setEditingSection(null);
    } catch {
      setActionError("Impossible de modifier le bloc.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDuplicate(s: LibrarySection) {
    setActionError(null);
    try {
      await createSection({
        title: `${s.title} (copie)`,
        content: s.content as JSONContent,
        category: s.category ?? "custom",
      });
      setOpenCategories((prev) => new Set(prev).add(s.category ?? "custom"));
    } catch {
      setActionError("Impossible de dupliquer le bloc.");
    }
  }

  async function handleDelete(id: string) {
    setActionError(null);
    try {
      await deleteSection(id);
    } catch {
      setActionError("Impossible de supprimer le bloc.");
    }
  }

  function exitEditMode() {
    setEditMode(false);
    setShowCreateForm(false);
    setEditingSection(null);
  }

  return (
    <PageLayout>
      {/* En-tête */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => navigate("/reglages")}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Retour"
          >
            <ArrowLeft className="size-5" />
          </button>
          <h2 className="text-xl font-bold text-foreground truncate">
            Ma librairie
          </h2>
        </div>

        {editMode ? (
          <Button size="sm" variant="outline" onClick={exitEditMode}>
            <Check className="size-4" />
            Terminer
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
            <Settings2 className="size-4" />
            Modifier la librairie
          </Button>
        )}
      </div>

      <p className="mb-4 text-sm text-muted-foreground">
        {editMode
          ? "Mode édition : vous pouvez ajouter, modifier, dupliquer ou supprimer n'importe quel bloc."
          : "Cliquez sur une catégorie pour voir ses blocs. Utilisez « Modifier la librairie » pour les éditer."}
      </p>

      {/* Mode édition : bouton Nouveau bloc */}
      {editMode && !showCreateForm && !editingSection && (
        <Button
          size="sm"
          className="mb-4"
          onClick={() => {
            setShowCreateForm(true);
            setEditingSection(null);
          }}
        >
          <Plus className="size-4" />
          Nouveau bloc
        </Button>
      )}

      {/* Formulaire de création */}
      {showCreateForm && (
        <div className="mb-6 rounded-lg border border-border bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Nouveau bloc</h3>
          <SectionForm
            categorySuggestions={categorySuggestions}
            onSave={handleCreate}
            onCancel={() => setShowCreateForm(false)}
            loading={saving}
          />
        </div>
      )}

      {/* Formulaire d'édition */}
      {editingSection && (
        <div className="mb-6 rounded-lg border border-primary/30 bg-card p-4">
          <h3 className="mb-3 text-sm font-semibold">Modifier le bloc</h3>
          <SectionForm
            initial={editingSection}
            categorySuggestions={categorySuggestions}
            onSave={handleUpdate}
            onCancel={() => setEditingSection(null)}
            loading={saving}
          />
        </div>
      )}

      {actionError && (
        <p className="mb-4 text-sm text-destructive">{actionError}</p>
      )}

      {loading && sections.length === 0 && (
        <p className="text-sm text-muted-foreground">Chargement…</p>
      )}

      {error && (
        <p className="mb-4 text-sm text-destructive">
          {error} (les données hors ligne sont affichées)
        </p>
      )}

      {/* Liste accordéon par catégorie */}
      {orderedCategories.map((cat) => {
        const isOpen = openCategories.has(cat);
        const count = byCategory[cat].length;
        return (
          <div key={cat} className="mb-3">
            <button
              type="button"
              onClick={() => toggleCategory(cat)}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2 text-left hover:bg-accent/40 transition-colors"
              aria-expanded={isOpen}
            >
              <span className="flex items-center gap-2">
                {isOpen ? (
                  <ChevronDown className="size-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="size-4 text-muted-foreground" />
                )}
                <span className="text-sm font-semibold text-foreground">
                  {labelFor(cat)}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  {count}
                </span>
              </span>
            </button>

            {isOpen && (
              <div className="mt-2 flex flex-col gap-2 pl-2">
                {byCategory[cat].map((section) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    editMode={editMode}
                    onEdit={(s) => {
                      setEditingSection(s);
                      setShowCreateForm(false);
                    }}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {sections.length === 0 && !loading && (
        <p className="text-sm text-muted-foreground">
          Aucun bloc dans la librairie pour l&apos;instant.
        </p>
      )}
    </PageLayout>
  );
}
